<template>
  <aside
    class="ai-panel"
    aria-label="Доказательства"
  >
    <header class="ai-panel__head">
      <div
        class="ai-segmented"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          :aria-selected="tab === 'cited' ? 'true' : 'false'"
          :class="{ 'is-active': tab === 'cited' }"
          :disabled="!ids.length && !extra.length"
          @click="tab = 'cited'"
        >
          В ответе{{ ids.length ? ` · ${ids.length}` : '' }}
        </button>
        <button
          type="button"
          role="tab"
          :aria-selected="tab === 'all' ? 'true' : 'false'"
          :class="{ 'is-active': tab === 'all' }"
          @click="openAll"
        >
          Все материалы{{ total ? ` · ${total}` : '' }}
        </button>
      </div>
      <button
        type="button"
        class="ai-icon-button"
        title="Закрыть панель"
        @click="$emit('close')"
      >
        <ai-icon name="close" />
      </button>
    </header>

    <div
      ref="scroller"
      class="ai-panel__body"
    >
      <template v-if="tab === 'cited'">
        <template v-for="(id, index) in shown">
          <p
            v-if="index === ids.length"
            :key="`extra-${id}`"
            class="ai-panel__section"
          >
            Связанные материалы
          </p>
          <component
            :is="cardOf(evidence[id])"
            v-if="ready(id)"
            :key="id"
            :item="evidence[id]"
            :number="index < ids.length ? index + 1 : null"
            :focused="id === focused"
            @open="openRef"
          />
          <div
            v-else
            :key="id"
            class="ai-panel__placeholder"
            :data-eid="id"
          >
            <span
              v-if="index < ids.length"
              class="ai-evidence-number"
            >
              {{ index + 1 }}
            </span>
            <span v-if="evidence[id] && evidence[id].unavailable">Материал недоступен: нет прав или он удалён</span>
            <span v-else-if="evidence[id] && evidence[id].error">{{ evidence[id].error }}</span>
            <span v-else>
              <span class="ai-spinner ai-spinner--small" />
              Загружаю…
            </span>
          </div>
        </template>
      </template>

      <template v-else>
        <component
          :is="cardOf(item)"
          v-for="item in all"
          :key="item.id"
          :item="item"
          :number="numbers[item.id] || null"
          :focused="item.id === focused"
          @open="openRef"
        />
        <p
          v-if="allError"
          class="ai-panel__note is-error"
        >
          {{ allError }}
        </p>
        <p
          v-else-if="!allLoading && !all.length"
          class="ai-panel__note"
        >
          Материалов пока нет.
        </p>
        <p
          v-if="hidden"
          class="ai-panel__note"
        >
          Скрыто материалов без доступа: {{ hidden }}.
        </p>
        <button
          v-if="all.length < total && !allLoading"
          type="button"
          class="ai-button ai-panel__more"
          @click="loadMore"
        >
          Показать ещё
        </button>
        <div
          v-if="allLoading"
          class="ai-panel__note"
        >
          <span class="ai-spinner ai-spinner--small" />
          Загружаю…
        </div>
      </template>
    </div>
  </aside>
</template>

<script>
  import { errorText, listEvidence } from '@/api/methods/assistant';
  import AiIcon from './AiIcon.vue';
  import EvidenceCard from './EvidenceCard.vue';
  import FindingCard from './FindingCard.vue';

  const PAGE = 30;

  export default {
    name: 'EvidencePanel',
    components: { AiIcon, EvidenceCard, FindingCard },
    props: {
      conversationId: { type: String, required: true },
      ids: { type: Array, default: () => [] },
      focus: { type: String, default: null },
      initialTab: { type: String, default: 'cited' },
    },
    data() {
      return {
        tab: this.ids.length ? this.initialTab : 'all',
        all: [],
        total: 0,
        hidden: 0,
        allLoading: false,
        allError: '',
        // Evidence opened from a card (the record of a read file, the records of a finding)
        // that the answer does not cite: shown after the cited ones.
        extra: [],
        focused: this.focus,
      };
    },
    computed: {
      evidence() {
        return this.$store.getters['assistant/evidenceFor'](this.conversationId);
      },
      numbers() {
        return this.ids.reduce((result, id, index) => ({ ...result, [id]: index + 1 }), {});
      },
      shown() {
        return [...this.ids, ...this.extra.filter((id) => !this.ids.includes(id))];
      },
    },
    watch: {
      ids: {
        immediate: true,
        handler(ids) {
          if (ids.length) {
            this.$store.dispatch('assistant/fetchEvidence', { id: this.conversationId, ids });
          } else if (this.tab === 'cited') {
            this.openAll();
          }
        },
      },
      focus: {
        immediate: true,
        handler(id) {
          this.focused = id;
          if (!id) return;
          if (this.ids.includes(id)) this.tab = 'cited';
          this.$nextTick(() => this.scrollTo(id));
        },
      },
      conversationId() {
        this.all = [];
        this.total = 0;
        this.extra = [];
      },
    },
    methods: {
      cardOf(item) {
        return item && item.card && item.card.kind === 'finding' ? 'finding-card' : 'evidence-card';
      },
      // A reference inside a card: the evidence is shown (loaded when needed) and focused.
      openRef(id) {
        if (!id) return;
        const listed = this.tab === 'all' && this.all.some((item) => item.id === id);
        if (!listed) {
          if (!this.ids.includes(id) && !this.extra.includes(id)) this.extra.push(id);
          this.$store.dispatch('assistant/fetchEvidence', { id: this.conversationId, ids: [id] });
          this.tab = 'cited';
        }
        this.focused = id;
        this.$nextTick(() => this.scrollTo(id));
      },
      ready(id) {
        const item = this.evidence[id];
        return !!(item && item.card);
      },
      scrollTo(id, attempt = 0) {
        const { scroller } = this.$refs;
        const target = scroller && scroller.querySelector(`[data-eid="${id}"]`);
        if (target) {
          target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } else if (attempt < 10) {
          setTimeout(() => this.scrollTo(id, attempt + 1), 150);
        }
      },
      openAll() {
        this.tab = 'all';
        if (!this.all.length) this.loadMore();
      },
      async loadMore() {
        this.allLoading = true;
        this.allError = '';
        try {
          const { data } = await listEvidence(this.conversationId, this.all.length, PAGE);
          this.all = [...this.all, ...(data.items || [])];
          this.total = data.total || this.all.length;
          this.hidden += data.hidden || 0;
        } catch (error) {
          this.allError = errorText(error, 'Не удалось загрузить материалы');
        } finally {
          this.allLoading = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-panel {
    display: flex;
    flex: 0 0 380px;
    flex-direction: column;
    min-width: 0;
    border-left: 1px solid var(--ui-border);
    background: var(--ui-bg);
  }

  .ai-panel__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--ui-border);
    background: var(--ui-surface);
  }

  .ai-panel__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
    padding: 12px;
    overflow-y: auto;
  }

  .ai-panel__placeholder {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    border: 1px dashed var(--ui-border-strong);
    border-radius: var(--ui-radius);
    color: var(--ui-text-muted);
    font-size: 13px;
  }

  .ai-panel__note {
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 13px;

    &.is-error {
      color: var(--ui-danger);
    }
  }

  .ai-panel__more {
    align-self: center;
  }

  .ai-panel__section {
    margin: 6px 0 -2px;
    color: var(--ui-text-muted);
    font-size: 12px;
    font-weight: 500;
  }
</style>
